import {addBreadcrumb} from '@sentry/browser';

import {isNotNil} from 'ramda';

import {sleep} from 'shared/web';

const NOT_FOUND_STATUS = 404;
const RETRY_DELAY_MS = 1000;
const MAX_ATTEMPTS = 3;

const isNotFoundError = (error: unknown) =>
  isNotNil(error) &&
  typeof error === 'object' &&
  'status' in error &&
  error.status === NOT_FOUND_STATUS;

/**
 * Fetches a background notification payload, tolerating the one legitimate
 * transient 404.
 *
 * The AppSync push leaves the backend just before the consumer's transaction
 * commits, so a fast client can beat the flush and read a notification that does
 * not exist yet. Retrying briefly closes that race so a fetch does not fail —
 * and surface an error to the user — for a notification that is actually about
 * to become available.
 *
 * Only closes that specific race: a non-404 error is not retried, and a 404
 * that never clears still resolves to `null`. Deciding what the user sees on
 * final failure is the caller's job, not this function's.
 */
export const fetchBackgroundNotificationWithRetry = async <TNotification>(
  fetchNotification: () => Promise<TNotification>
): Promise<TNotification | null> => {
  let attemptsLeft = MAX_ATTEMPTS;

  while (attemptsLeft > 0) {
    attemptsLeft -= 1;

    try {
      return await fetchNotification();
    } catch (error) {
      const canRetry = isNotFoundError(error) && attemptsLeft > 0;

      if (!canRetry) {
        addBreadcrumb({
          category: 'appsync',
          level: 'warning',
          message: 'Background notification payload could not be fetched',
        });
        return null;
      }

      await sleep(RETRY_DELAY_MS);
    }
  }

  return null;
};
